import { useEffect } from "react";

export type DocsHoundChatProps = {
  /**
   * Domain where the guide is published. Eg: https://docs.docshound.com
   *
   * This field is not reactive, so if you change it, you need to reload the page.
   *
   * Required field;
   */
  src: string;

  /**
   * The name of the agent responding in the chat.
   *
   * Optional field. Defaults to value set in the DocsHound.
   */
  agentName?: string;

  /**
   * The URL for the avatar image of the agent.
   *
   * Optional field. Defaults to value set in the DocsHound.
   */
  agentAvatarUrl?: string;

  /**
   * A unique identifier for the user interacting with the chat.
   * This should be unique identifier coming from your system.
   * Used to identify the user and their support tickets.
   *
   * Optional field. Defaults to undefined.
   */
  userId?: string;

  /**
   * The email address of the user interacting with the chat.
   * Will be pre-filled for the user when they decide to submit a support ticket.
   *
   * Optional field. Defaults to undefined.
   */
  userEmail?: string;

  /**
   * The display name of the user interacting with the chat.
   *
   * Optional field. Defaults to 'You'.
   */
  userName?: string;

  /**
   * The platform where the chat is being used. Could be either 'site' (marketing site), 'app' (application), or 'docs' (documentation site / platform).
   *
   * Optional field, defaults to 'app'.
   */
  platform?: "site" | "app" | "docs";

  /**
   * The URL for the avatar image of the user interacting with the chat.
   *
   * Optional field. Defaults to generic user avatar.
   */
  userAvatarUrl?: string;

  /**
   * The initial agent message that will be prepopulated when user opens the chatbot widget.
   *
   * Optional field. Default to value set in DocsHound.
   */
  firstMessage?: string;

  /**
   * Defines the theme of the chat interface, either 'light' or 'dark'.
   *
   * Optional field. Defaults to value set in DocsHound.
   */
  theme?: "light" | "dark";

  /**
   * Defines the position of the chat trigger on the screen. Can be either 'right', 'right-bottom' or 'right-top'.
   *
   * Optional field. Defaults to 'right'
   */
  position?: "right" | "right-top" | "right-bottom";

  /**
   * Sets the accent color of the chat interface (e.g., for buttons or highlights).
   *
   * Optional field. Defaults to value set in DocsHound.
   */
  accentColor?: string;

  /**
   * The label for the trigger button that starts the chat.
   *
   * Optional field. Defaults to value set in DocsHound.
   */
  triggerLabel?: string;

  /**
   * Authentication token for jwt / password / basic protected guides
   *
   * Optional field.
   */
  token?: string;

  /**
   * Authentication token for additional support MCP server
   *
   * Optional field.
   */
  mcpToken?: string;
};

export const DocsHoundChat = ({
  src,
  agentName,
  agentAvatarUrl,
  accentColor,
  platform,
  position,
  theme,
  triggerLabel,
  userAvatarUrl,
  userId,
  userEmail,
  userName,
  firstMessage,
  token,
  mcpToken,
}: DocsHoundChatProps) => {
  const id = `__DH_CHAT_SCRIPT__`;

  useEffect(() => {
    if (!customElements.get("docshound-chat")) {
      let existingScript = document.getElementById(
        id
      ) as HTMLScriptElement | null;

      if (!existingScript) {
        let finalSrc = "";

        if (src.endsWith("/-/chat.js")) {
          finalSrc = src;
        } else if (src.endsWith("/")) {
          finalSrc = `${src}-/chat.js`;
        } else {
          finalSrc = `${src}/-/chat.js`;
        }

        const script = document.createElement("script");
        script.src = finalSrc;
        script.id = id;
        script.type = "text/javascript";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }
  }, []);

  return (
    <docshound-chat
      agent-name={agentName}
      agent-avatar-url={agentAvatarUrl}
      accent-color={accentColor}
      platform={platform}
      position={position}
      theme={theme}
      trigger-label={triggerLabel}
      user-id={userId}
      user-email={userEmail}
      user-name={userName}
      user-avatar-url={userAvatarUrl}
      first-message={firstMessage}
      token={token}
      mcp-token={mcpToken}
    />
  );
};
